import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  Theme,
  Paper,
  Grid,
  TextField,
  RadioGroup,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  Button,
  Tooltip,
  CircularProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import RateReviewIcon from "@mui/icons-material/RateReview";
import { useData } from "../../providers/DataProvider";
import {
  CommentProps,
  fileAttachmentsByTypesProps,
  fileAttachmentsProps,
  JobDetailsProps,
  RoleType,
} from "../../providers/types";
import theme from "../../theme";
import axiosInstance from "../../utils/axiosInstance";
import { useMsal } from "@azure/msal-react";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";
import { lightBlue } from "@mui/material/colors";
import { UIContext } from "../../providers/UIProvider";

interface Props {
  jobId: number;
}

// Ex-employee constant
const EX_EMPLOYEE_BUILDER = {
  builderName: "Ex Employee",
  employeeNumber: 999999,
  builderEmail: "noreply@highlandhomes.com",
};

const ReviewerViewModal = ({ jobId }: Props) => {
  const [state, dispatch] = React.useContext<any>(UIContext);
  const { selectedRegion, loadBonuses, role, bonusType } = useData();
  const { accounts } = useMsal();
  const [open, setOpen] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [files, setFiles] = useState<any[]>([]);
  const [job, setJob] = useState<JobDetailsProps | undefined>(undefined);
  const [reviewDecision, setReviewDecision] = useState("");
  const [commentText, setCommentText] = useState("");
  const [fileCommentText, setFileCommentText] = useState("");
  const [fileStreamUrl, setFileStreamUrl] = useState<string | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(false);
  const [viewHistory, setViewHistory] = useState(false);
  const [viewPending, setViewPending] = useState(true);
  const [historyComments, setHistoryComments] = useState<
    {
      action: string;
      comment: string;
      createdBy: string;
      createdOn: string;
      fileAttachmentId: number;
      fileAttachmentName: string;
      fileAttachmentTypeName: string;
      id: number;
      jobId: number;
      roleTypeId: number;
    }[]
  >([]);
  const [filecomments, setFileComments] = useState<CommentProps[]>([]);

  const [isReadyOnly, setIsReadyOnly] = useState<boolean>(true);
  const selectedRole = localStorage.getItem("cba.role") || "";

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFiles([]);
    setFileStreamUrl(undefined);
    if (fileStreamUrl) {
      URL.revokeObjectURL(fileStreamUrl); // Revoke the object URL to free up memory
    }
  };

  const generateUniqueId = () => {
    return `${Date.now()}-${Math.floor(Math.random() * 10000)}`;
  };

  const generateUniqueNumberId = () => {
    // Use only last 5 digits of timestamp + 4 digit random
    const timestamp = Date.now() % 100000;
    const random = Math.floor(Math.random() * 10000);
    return timestamp * 10000 + random;
  }

  const handleNext = () => {
    if (files && currentFileIndex < files.length - 1) {
      setCurrentFileIndex(currentFileIndex + 1);
      fetchFileStream(files[currentFileIndex + 1].fileAttachmentId);
    }
  };

  const handlePrevious = () => {
    if (currentFileIndex > 0) {
      setCurrentFileIndex(currentFileIndex - 1);
      fetchFileStream(files[currentFileIndex - 1].fileAttachmentId);
    }
  };

  const handleAddFileComment = () => {
    if (!fileCommentText.trim()) {
      alert("Please enter a comment");
      return;
    }

    // Only process file comments if we have files (not Stage5Bonus)
    if (files.length > 0) {
      const currentFile = files[currentFileIndex];
      const fileType = job?.fileAttachmentTypes.find(
        (type) =>
          type.fileAttachmentTypesId === currentFile?.fileAttachmentTypeId
      );

      setFileComments([
        ...filecomments,
        {
          Id: generateUniqueNumberId(),
          JobId: jobId,
          RoleTypeId: -1,
          Action: "Comment",
          Comment: fileCommentText,
          CreatedBy: accounts[0].username,
          FileAttachmentId: files[currentFileIndex]?.fileAttachmentId,
          BonusTypeId: bonusType === "Stage5Bonus" ? 1 : 2,
          Name: fileType ? `${fileType.name} ${currentFile.fileName}` : ""
        },
      ]);
    } else {
      // For Stage5Bonus, just add a general comment without file reference
      setFileComments([
        ...filecomments,
        {
          Id: generateUniqueNumberId(),
          JobId: jobId,
          RoleTypeId: -1,
          Action: "Comment",
          Comment: fileCommentText,
          CreatedBy: accounts[0].username,
          FileAttachmentId: 0,
          BonusTypeId: bonusType === "Stage5Bonus" ? 1 : 2,
          Name: "General Comment",
        },
      ]);
    }

    setFileCommentText("");
  };

  const handleSubmit = () => {
    if (!reviewDecision) {
      let message = `Please select ${bonusType === "Stage5Bonus" && selectedRole !== "OpsPlus" ? "approve" : "approve or reject"}.`;

      const closingConditions = bonusType !== "Stage5Bonus" &&
        (
          // Disable if no builders assigned
          !job?.builders || job.builders.length === 0 ||
          // For OpsPlus role, also require valid close date
          ((selectedRole === "OpsPlus" || selectedRole === "Ops") &&
            (!job?.closeDate || job.closeDate === "0001-01-01T00:00:00"))
        );


      if (closingConditions) {
        message = `Please select Reject and enter comment.\n\nNote: Approve cannot be selected - See Notes section for details`;
      }

      alert(message);
      return;
    }

    if (reviewDecision === "reject" && !commentText.trim()) {
      alert("Comment is required for rejection.");
      return;
    }

    if (window.confirm("Are you sure you want to submit your review?")) {
      const updateData = {
        JobId: jobId,
        BonusStatus: reviewDecision === "reject" ? "Reject" : "Approve",
        UpdatedBy: accounts[0].username,
        Comment: commentText,
        FileAttachmentComments: filecomments,
      };

      axiosInstance
        .put(`/api/cba/bonuses/${jobId}`, updateData)
        .then(() => {
          if (role) {
            loadBonuses(role);
          }
        })
        .catch((error) => {
          console.error("Error submitting review:", error);
        });

      setOpen(false);
      setCommentText("");
      setReviewDecision("");
    }
  };

  // Submit handler for OpsApproved to InOpsReview or No Action with comment
  const handleSubmit2 = () => {
    if (!reviewDecision) {
      const message = "Action required.\nTo revert this job, please select \"  - Revert to Ops Review (for corrections)\" and provide a comment.\nTo add a comment without action, please select \"  - No Action - Just add a comment\".";
      alert(message);
      return;
    }

    if (reviewDecision === "opsApprovedToInOpsReview" && !commentText.trim()) {
      alert("Comment required.\nPlease enter a comment explaining the reason for reverting to In Ops Review Status.");
      return;
    }

    if (reviewDecision === "noActionJustComment" && !commentText.trim()) {
      alert("Comment required.\nPlease enter a comment.");
      return;
    }

    if (window.confirm("Are you sure you want to submit your review?")) {

      if (reviewDecision === "noActionJustComment") {

        let comments: CommentProps[] = createCommentList(commentText, filecomments, bonusType, "NoActionJustComment");

        axiosInstance
          .post(`/api/cba/comments`, comments)
          .then(() => {
            if (role) {
              loadBonuses(role);
            }
          })
          .catch((error) => {
            console.error("Error submitting comment:", error);
          });

      } else if (reviewDecision === "opsApprovedToInOpsReview") {
        const updateData = {
          JobId: jobId,
          BonusStatus: "Revert",
          UpdatedBy: accounts[0].username,
          Comment: "Reverted from Ops-Approved to In-Ops-Review: " + commentText,
          FileAttachmentComments: filecomments,
        };

        axiosInstance
          .put(`/api/cba/bonuses/${jobId}`, updateData)
          .then(() => {
            dispatch({
              type: "Snackbar",
              payload: {
                show: true,
                message: "Successfully updated.",
                severity: "success",
              },
            });
            setTimeout(() => {
              window.location.reload();
            }, 3000);

            if (role) {
              loadBonuses(role);
            }
          })
          .catch((error) => {
            console.error('Error submitting review:', error);
            console.error('Response data:', error.response?.data);
            console.error('Response status:', error.response?.status);
            console.error('Submitting payload:', JSON.stringify(updateData, null, 2));
          });
      }

      setOpen(false);
      setCommentText("");
      setReviewDecision("");
    }
  };

  const createCommentList = (commentText: string, filecomments: CommentProps[], bonusType: string, action: string): CommentProps[] => {
    let comments: CommentProps[] = [];

    filecomments.forEach((fc) => {
      let comment: CommentProps = {
        Id: generateUniqueNumberId(),
        JobId: jobId,
        RoleTypeId: -1,
        Action: action,
        Comment: fc.Comment,
        CreatedBy: accounts[0].username,
        FileAttachmentId: fc.FileAttachmentId,
        BonusTypeId: bonusType === "Stage5Bonus" ? 1 : 2,
        Name: fc.Name
      };
      comments.push(comment);
    });

    if (commentText.trim()) {
      let comment: CommentProps = {
        Id: generateUniqueNumberId(),
        JobId: jobId,
        RoleTypeId: -1,
        Action: action,
        Comment: commentText,
        CreatedBy: accounts[0].username,
        FileAttachmentId: 0,
        BonusTypeId: bonusType === "Stage5Bonus" ? 1 : 2,
        Name: "General Comment"
      };
      comments.push(comment);
    }
    return comments;
  }



  // Invoked for InPayrollProcess and Paid statuses
  // Here, only comments are added without changing status
  const handleSubmit3 = () => {

    if (!commentText.trim()) {
      alert("Comment is required to submit.");
      return;
    }

    if (window.confirm("Are you sure you want to submit your comment?")) {
      if (commentText.trim()) {

        let comments: CommentProps[] = createCommentList(commentText, filecomments, bonusType, "NoActionJustComment");

        axiosInstance
          .post(`/api/cba/comments`, comments)
          .then(() => {
            if (role) {
              loadBonuses(role);
            }
          })
          .catch((error) => {
            console.error("Error submitting comment:", error);
          });

      }

      setOpen(false);
      setCommentText("");
      setReviewDecision("");
    }
  };

  const fetchFileStream = (fileAttachmentId: number) => {
    setLoading(true);
    axiosInstance
      .get(`/api/cba/attachmentfile/${fileAttachmentId}`, {
        responseType: "blob",
      })
      .then((response) => {
        const url = URL.createObjectURL(response.data);
        setFileStreamUrl(url);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching file stream:", error);
        setLoading(false);
      });
  };

  const loadJobDetails = (jobId: number, role: string | null) => {
    axiosInstance
      .get(`/api/cba/job-details/${jobId}`, {
        headers: {
          roleType: role,
        },
      })
      .then((response) => {
        // Reorder builders to put ex-employee at the end
        let builders = response.data?.builders || [];

        const exEmployeeIndex = builders.findIndex(
          (builder: any) =>
            builder.builderName === EX_EMPLOYEE_BUILDER.builderName &&
            builder.builderEmail === EX_EMPLOYEE_BUILDER.builderEmail &&
            builder.employeeNumber === EX_EMPLOYEE_BUILDER.employeeNumber
        );

        if (exEmployeeIndex !== -1) {
          // Remove ex-employee from its current position
          const exEmployee = builders.splice(exEmployeeIndex, 1)[0];
          // Add ex-employee to the end
          builders.push(exEmployee);
        }

        // Update the response data with reordered builders
        if (response.data?.bonusDetail?.bonus) {
          response.data.builders = builders;
        }


        setJob(response.data);
        setIsReadyOnly(response.data.bonusDetail.isReadyOnly);

        // Skip loading files if it's a Stage5Bonus type
        if (bonusType !== "Stage5Bonus") {
          let fileAttachments: fileAttachmentsProps[] = [];
          if (response?.data?.fileAttachmentsByTypes?.length > 0) {
            response.data.fileAttachmentsByTypes.forEach(
              (fat: fileAttachmentsByTypesProps) => {
                if (fat?.fileAttachments?.length > 0) {
                  const hasMergedFile = fat.fileAttachments.find(
                    (file) => file.isMergedFile
                  );
                  if (hasMergedFile) {
                    fileAttachments.push(hasMergedFile);
                  } else {
                    fat.fileAttachments.forEach((fa) => {
                      fileAttachments.push(fa);
                    });
                  }
                }
              }
            );
          }

          setFiles(fileAttachments);
          if (fileAttachments.length > 0) {
            fetchFileStream(fileAttachments[0].fileAttachmentId);
          }
        } else {
          // For Stage5Bonus, set an empty files array
          setFiles([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching job details:", error);
      });
  };

  const loadComments = async (jobId: number) => {
    if (jobId) {
      axiosInstance.get(`/api/cba/comments/${jobId}`).then((response) => {
        setHistoryComments(response.data);
      });
    }
  };

  useEffect(() => {
    if (open) {
      loadJobDetails(jobId, role);
      loadComments(jobId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId, role]);

  const isPdfFile = files[currentFileIndex]?.fileName
    .toLowerCase()
    .endsWith(".pdf");

  return (
    <>
      <Tooltip title="Review">
        <IconButton onClick={handleOpen} size="small">
          <RateReviewIcon className="h-5 w-5 text-primary-600" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={bonusType === "Stage5Bonus" ? "md" : "xl"}
        fullWidth
      >
        {job && (
          <Grid container>
            {/* Left Panel - PDF Viewer - Only shown when not Stage5Bonus */}
            {bonusType !== "Stage5Bonus" && (
              <Grid item xs={7} sx={{ borderRight: 1, borderColor: "divider" }}>
                <DialogTitle
                  sx={{ borderBottom: 1, borderColor: "divider", p: { xs: 1.5, sm: 2 } }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontSize: { xs: "0.95rem", sm: "1.25rem" }, minWidth: 0, flex: 1 }}>
                      {job.jobAddress} - {job.subdivision} [{job.projectNumber}-{job.jobNumber}]
                    </Typography>
                    <IconButton onClick={handleClose} size="small" sx={{ flexShrink: 0 }}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </DialogTitle>

                {/* File Navigation */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      onClick={handlePrevious}
                      disabled={currentFileIndex === 0 || loading}
                    >
                      <NavigateBeforeIcon />
                    </IconButton>
                    <Typography
                      variant="subtitle1"
                      component="div"
                      textAlign="center"
                    >
                      {files[currentFileIndex]
                        ? `${job?.fileAttachmentTypes.find(
                          (type) =>
                            type.fileAttachmentTypesId ===
                            files[currentFileIndex].fileAttachmentTypeId
                        )?.name || ""
                        } - ${files[currentFileIndex].fileName}`
                        : "No File"}
                    </Typography>
                    <IconButton
                      onClick={handleNext}
                      disabled={
                        currentFileIndex === files.length - 1 || loading
                      }
                    >
                      <NavigateNextIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* PDF Viewer */}
                <Box sx={{ height: "calc(100vh - 250px)", overflow: "auto" }}>
                  {loading ? (
                    <CircularProgress />
                  ) : isPdfFile ? (
                    <Box
                      sx={{
                        display: "flex",
                        flex: 1,
                        height: "100%",
                      }}
                    >
                      <iframe
                        src={fileStreamUrl}
                        width="100%"
                        height="100%"
                        title="pdf preview"
                      />
                    </Box>
                  ) : (
                    <img
                      src={fileStreamUrl}
                      alt={files[currentFileIndex]?.fileName}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "70vh", // Set max height relative to view height
                        objectFit: "contain",
                      }}
                    />
                  )}
                </Box>
              </Grid>
            )}

            {/* Right Panel - Review Information */}
            <Grid item xs={bonusType === "Stage5Bonus" ? 12 : 5}>
              {bonusType === "Stage5Bonus" && (
                <DialogTitle
                  sx={{ borderBottom: 1, borderColor: "divider", p: { xs: 1.5, sm: 2 } }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: { xs: "0.95rem", sm: "1.25rem" } }}>
                        {job.jobAddress} - {job.subdivision} [{job.projectNumber}-{job.jobNumber}]
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.25,
                          color: "grey.600",
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        }}
                      >
                        Job ID: {job.jobId}
                      </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small" sx={{ flexShrink: 0 }}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </DialogTitle>
              )}
              <Box sx={{ height: "100vh", overflow: "auto" }}>
                {/* Builder Split Information - Card Layout */}
                <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: "background.paper" }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 0, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                    Builder Split Information - {bonusType === "Stage5Bonus" ? "Stage5 Bonus" : "Closing Bonus"}
                  </Typography>
                  {bonusType !== "Stage5Bonus" && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: { xs: 1.5, sm: 2 },
                        color: "grey.600",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                    >
                      Job ID: {job.jobId}
                    </Typography>
                  )}

                  {/* Closing and Stage5 Cards Side by Side */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {/* Closing Card */}
                    {(bonusType !== "Stage5Bonus" && <Grid item xs={12} sm={12} md={6}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          height: "100%",
                          bgcolor: bonusType !== "Stage5Bonus" ? "primary.50" : "grey.50",
                          border: bonusType !== "Stage5Bonus" ? 2 : 1,
                          borderColor: bonusType !== "Stage5Bonus" ? "primary.main" : "divider",
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: { xs: 1.5, sm: 2 }, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
                          Closing
                        </Typography>

                        <Stack spacing={{ xs: 1, sm: 1.5 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, flex: 1 }}>
                              Closing Bonus Amount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: { xs: "0.8rem", sm: "0.875rem" }, textAlign: "right" }}>
                              {job?.bonusDetail?.bonusAmounts?.closingBonus
                                ? `$${job.bonusDetail.bonusAmounts.closingBonus.toLocaleString('en-US', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}`
                                : "$0.00"}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, flex: 1 }}>
                              Sales Price
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, textAlign: "right" }}>
                              {job?.salesPrice && job.salesPrice > 0
                                ? job.salesPrice.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })
                                : "N/A"}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, flex: 1 }}>
                              Close Date
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, textAlign: "right" }}>
                              {job?.closeDate && job.closeDate !== "0001-01-01T00:00:00"
                                ? new Date(job.closeDate).toLocaleDateString("en-US")
                                : "N/A"}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>)}

                    {/* Stage5 Card */}
                    <Grid item xs={12} sm={12} md={6}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          height: "100%",
                          bgcolor: bonusType === "Stage5Bonus" ? "primary.50" : "grey.50",
                          border: bonusType === "Stage5Bonus" ? 2 : 1,
                          borderColor: bonusType === "Stage5Bonus" ? "primary.main" : "divider",
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: { xs: 1.5, sm: 2 }, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
                          Stage5
                        </Typography>

                        <Stack spacing={{ xs: 1, sm: 1.5 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, flex: 1 }}>
                              Stage5 Bonus Amount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: { xs: "0.8rem", sm: "0.875rem" }, textAlign: "right" }}>
                              {job?.bonusDetail?.bonusAmounts?.stage5Bonus?.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }) || "N/A"}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, flex: 1 }}>
                              Stage5 Price
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, textAlign: "right" }}>
                              {job?.stage5BonusPrice?.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }) || "N/A"}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, flex: 1 }}>
                              Stage5 Date
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, textAlign: "right" }}>
                              {job?.stage5Date && job.stage5Date !== "0001-01-01T00:00:00"
                                ? new Date(job.stage5Date).toLocaleDateString("en-US")
                                : "N/A"}
                            </Typography>
                          </Box>

                          {job?.bonusDetail?.stage5Payroll?.amount && (
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, flex: 1 }}>
                                Payroll Paid Amount
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, textAlign: "right" }}>
                                {job.bonusDetail.stage5Payroll?.amount?.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                }) || "N/A"}
                                {
                                  (job?.bonusDetail?.stage5Payroll?.multiCorrectionCount ?? 0) > 0 &&
                                  ` [${job.bonusDetail.stage5Payroll.multiCorrectionCount}]`}
                              </Typography>
                            </Box>
                          )}

                          {job?.bonusDetail?.stage5Payroll?.payDate &&
                            job.bonusDetail.stage5Payroll?.payDate !== "0001-01-01T00:00:00" && (
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, flex: 1 }}>
                                  Payroll Paid Date
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, textAlign: "right" }}>
                                  {new Date(job.bonusDetail.stage5Payroll?.payDate).toLocaleDateString("en-US")}
                                </Typography>
                              </Box>
                            )}
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Notes Card */}
                  {bonusType === "ClosingBonus" &&
                    (
                      job?.bonusDetail?.isBustedAndResold ||
                      job?.bonusDetail?.wasRejected ||
                      job?.bonusDetail?.isModelHome ||
                      !job?.builders?.length ||
                      (!job?.closeDate || job.closeDate === "0001-01-01T00:00:00") ||
                      (
                        job?.bonusDetail?.stage5Payroll?.amount &&
                        job?.bonusDetail?.bonusAmounts?.stage5Bonus &&
                        Math.abs(job.bonusDetail.stage5Payroll?.amount -
                          job.bonusDetail.bonusAmounts.stage5Bonus) > 0.01
                      )
                    )
                    && (<Paper
                      elevation={2}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        mb: 2,
                        bgcolor: "info.50",
                        border: 1,
                        borderColor: "info.main",
                      }}
                    >
                      <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "0.95rem", sm: "1rem" },
                            mb: 0.25
                          }}
                        >
                          Notes
                        </Typography>

                        {/* <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block"
                          }}
                        >
                          {job.jobAddress} - {job.subdivision} - {selectedRegion?.regionName}
                          <br />
                          [{job.projectNumber}-{job.jobNumber} - {job.jobId}]
                        </Typography> */}
                      </Box>

                      <Stack spacing={{ xs: 0.75, sm: 1 }}>
                        {job?.bonusDetail?.isBustedAndResold && (
                          <Chip
                            label="Busted and Resold"
                            size="small"
                            color="warning"
                            sx={{
                              fontWeight: "medium", fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                              justifyContent: "flex-start",
                              "& .MuiChip-label": {
                                whiteSpace: "normal",
                                lineHeight: 1.3,
                                textAlign: "left"
                              }
                            }}
                          />
                        )}

                        {job?.bonusDetail?.wasRejected && (
                          <Chip
                            label="Was Rejected and Resubmitted"
                            size="small"
                            color="info"
                            sx={{
                              fontWeight: "medium",
                              fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                              justifyContent: "flex-start",
                              "& .MuiChip-label": {
                                whiteSpace: "normal",
                                lineHeight: 1.3,
                                textAlign: "left"
                              }
                            }}
                          />
                        )}


                        {job?.bonusDetail?.isModelHome && (
                          <Chip
                            label="Model Home"
                            size="small"
                            color="info"
                            sx={{
                              fontWeight: "medium", fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                              justifyContent: "flex-start",
                              "& .MuiChip-label": {
                                whiteSpace: "normal",
                                lineHeight: 1.3,
                                textAlign: "left"
                              }
                            }}
                          />
                        )}

                        {(!job?.builders || job.builders.length === 0 && reviewDecision !== "opsApprovedToInOpsReview") && (
                          <Chip
                            label="No Builders Assigned - Approval Not Allowed"
                            size="small"
                            color="error"
                            sx={{
                              fontWeight: "medium",
                              fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                              height: "auto",
                              py: 0.5,
                              "& .MuiChip-label": {
                                whiteSpace: "normal",
                                lineHeight: 1.3,
                                textAlign: "left"
                              }
                            }}
                          />
                        )}

                        {job?.reviewStatus !== "OpsApproved" &&
                          job?.reviewStatus !== "InPayrollProcess" &&
                          job?.reviewStatus !== "Paid" &&
                          (!job?.closeDate || job.closeDate === "0001-01-01T00:00:00") && (
                            <Chip
                              label="Job Not Closed Yet - Ops-Approval Not Allowed Till Job Closed"
                              size="small"
                              color="error"
                              sx={{
                                fontWeight: "medium",
                                fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                                height: "auto",
                                py: 0.5,
                                "& .MuiChip-label": {
                                  whiteSpace: "normal",
                                  lineHeight: 1.3,
                                  textAlign: "left"
                                }
                              }}
                            />
                          )}

                        {job?.bonusDetail?.stage5Payroll &&
                          job?.bonusDetail?.stage5Payroll?.amount &&
                          job?.bonusDetail?.bonusAmounts?.stage5Bonus &&
                          Math.abs(job.bonusDetail.stage5Payroll?.amount -
                            job.bonusDetail.bonusAmounts.stage5Bonus) > 0.01 && (
                            <Chip
                              label="Payroll Paid Amount Different from Computed Stage5 Amount - Please Review"
                              size="small"
                              color="error"
                              sx={{
                                fontWeight: "medium",
                                fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                                height: "auto",
                                py: 0.5,
                                "& .MuiChip-label": {
                                  whiteSpace: "normal",
                                  lineHeight: 1.3,
                                  textAlign: "left"
                                }
                              }}
                            />
                          )}

                        {!job?.bonusDetail?.isBustedAndResold &&
                          !job?.bonusDetail?.wasRejected &&
                          !job?.bonusDetail?.isModelHome &&
                          job?.builders?.length > 0 &&
                          (job?.closeDate && job.closeDate !== "0001-01-01T00:00:00") &&
                          !(job?.bonusDetail?.stage5Payroll?.amount &&
                            job?.bonusDetail?.bonusAmounts?.stage5Bonus &&
                            Math.abs(job.bonusDetail.stage5Payroll?.amount -
                              job.bonusDetail.bonusAmounts.stage5Bonus) > 0.01) && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontStyle: "italic",
                                fontSize: { xs: "0.8rem", sm: "0.875rem" }
                              }}
                            >
                              No notes to display
                            </Typography>
                          )}
                      </Stack>
                    </Paper>)}

                  {/* Builder List */}
                  <List
                    sx={{
                      width: "100%",
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    {job?.builders?.map(
                      (builder: any, index: number) => (
                        <ListItem
                          key={index}
                          sx={{
                            flexDirection: "column",
                            alignItems: "flex-start",
                            py: { xs: 1.5, sm: 2 },
                            px: { xs: 1.5, sm: 2 },
                            ...(index !==
                              job.builders.length - 1 && {
                              borderBottom: 1,
                              borderColor: "divider",
                            }),
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              mb: { xs: 1, sm: 1 },
                            }}
                          >
                            {builder.employeeNumber === EX_EMPLOYEE_BUILDER.employeeNumber ? (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                <Box sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  bgcolor: "primary.50",
                                  // border: 1,
                                  // borderColor: "primary.200",
                                  // borderRadius: 1,
                                  // px: 1,
                                  // py: 0.25,
                                }}>
                                  <Typography variant="subtitle2" sx={{
                                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                                    fontWeight: 700,
                                    color: "primary.800",
                                    letterSpacing: 0.2,
                                  }}>
                                    Unassigned Bonus
                                  </Typography>
                                </Box>
                                {builder.builderName && builder.builderName !== "Unassigned Bonus" && (
                                  <Typography variant="caption" sx={{
                                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                    color: "primary.700",
                                    pl: 0.5,
                                    wordBreak: "break-word",
                                  }}>
                                    {builder.builderName}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="subtitle1" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                                {builder.builderName}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{ display: "flex", gap: { xs: 1, sm: 2 }, flexWrap: "wrap" }}
                          >
                            <Chip
                              label={`Split: ${builder.split}%`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: { xs: "0.7rem", sm: "0.8125rem" } }}
                            />
                            {builder.splitAmount > 0 && (
                              <Chip
                                label={`Split Amount: $${builder.splitAmount.toLocaleString()}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: { xs: "0.7rem", sm: "0.8125rem" } }}
                              />
                            )}
                            {builder.deductionAmount > 0 && (
                              <Chip
                                label={`Deduction: $${builder.deductionAmount.toLocaleString()}`}
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ fontSize: { xs: "0.7rem", sm: "0.8125rem" } }}
                              />
                            )}
                            {builder.splitAmount > 0 && (
                              <Chip
                                label={`${bonusType === "Stage5Bonus"
                                  ? "Final Stage5 Amount: "
                                  : "Final Closing Amount: "
                                  }$${(builder.splitAmount - builder.deductionAmount).toLocaleString()}`}
                                size="small"
                                sx={{
                                  bgcolor: lightBlue[100],
                                  fontSize: { xs: "0.7rem", sm: "0.8125rem" }
                                }}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: { xs: 0.75, sm: 1 }, fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                          >
                            {builder.builderEmail}
                          </Typography>
                        </ListItem>
                      )
                    )}
                  </List>
                </Box>

                {/* Comments History Section - Improved UI */}
                <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <List>
                    <ListItem
                      button
                      onClick={() => setViewHistory(!viewHistory)}
                      sx={{ bgcolor: "grey.50", borderRadius: 1, px: { xs: 1.5, sm: 2 } }}
                    >
                      <ListItemText
                        primary="Review History"
                        primaryTypographyProps={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                      />
                      {viewHistory ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={viewHistory} timeout="auto">
                      <List sx={{ maxHeight: 300, overflow: "auto" }}>
                        {historyComments ? (
                          historyComments.map((comment) => (
                            <Box
                              key={comment?.id}
                              sx={{
                                mt: 1,
                                px: { xs: 1.5, sm: 2 },
                                wordWrap: "normal",
                                wordBreak: "break-word",
                              }}
                            >
                              <Typography variant="body2" color="BlackText" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                                {comment?.createdBy} ({RoleType[comment?.roleTypeId] || "Unknown"})
                              </Typography>
                              <Typography
                                variant="caption"
                                color="GrayText"
                                sx={{
                                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                }}
                              >
                                {comment?.fileAttachmentTypeName ||
                                  "General Comment"}{" "}
                              </Typography>
                              <Box my={1} sx={{ pl: 0.5 }}>
                                {(() => {
                                  const raw = comment?.comment || "";
                                  const parts = raw.replace(/\s*\|\s*/g, "|")
                                    .split("|")
                                    .map(s => s.trim())
                                    .filter(Boolean);

                                  const KNOWN_LABELS = [
                                    "Builder Split Update:",
                                    "Bonus Deduction Explanation:",
                                    "Unassigned Bonus Explanation:",
                                    "Unassigned Bonus Reference:",
                                    "Payroll Cycle Skipped:",
                                    "Payroll Cycle Unskipped:",
                                    "Payroll Exclusion Added:",
                                    "Payroll Exclusion Removed:"
                                  ];

                                  const hasStructure = parts.some(p =>
                                    KNOWN_LABELS.some(label => p.startsWith(label))
                                  );

                                  if (!hasStructure) {
                                    return (
                                      <Typography variant="caption" color="CaptionText"
                                        sx={{ fontSize: { xs: "0.75rem", sm: "0.8125rem" } }}>
                                        {raw}
                                      </Typography>
                                    );
                                  }

                                  return (
                                    <Stack spacing={0.75}>
                                      {parts.map((part, i) => {
                                        const matchedLabel = KNOWN_LABELS.find(label => part.startsWith(label));
                                        if (matchedLabel) {
                                          const labelText = matchedLabel.replace(/:$/, "");
                                          const bodyText = part.slice(matchedLabel.length).trim();
                                          const isHeader = matchedLabel === "Builder Split Update:";
                                          return (
                                            <Box key={i}>
                                              <Typography variant="caption" sx={{
                                                fontWeight: 700,
                                                fontSize: { xs: "0.7rem", sm: "0.775rem" },
                                                color: isHeader ? "text.secondary" : "primary.700",
                                                textTransform: "uppercase",
                                                letterSpacing: 0.4,
                                                display: "block",
                                              }}>
                                                {labelText}
                                              </Typography>
                                              {bodyText && (
                                                <Typography variant="caption" color="CaptionText" sx={{
                                                  fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                                                  display: "block",
                                                  wordBreak: "break-word",
                                                }}>
                                                  {bodyText}
                                                </Typography>
                                              )}
                                            </Box>
                                          );
                                        }
                                        // Unlabeled segment
                                        return (
                                          <Typography key={i} variant="caption" color="CaptionText"
                                            sx={{ fontSize: { xs: "0.75rem", sm: "0.8125rem" }, wordBreak: "break-word" }}>
                                            {part}
                                          </Typography>
                                        );
                                      })}
                                    </Stack>
                                  );
                                })()}
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="GrayText"
                                  textAlign="right"
                                  sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                                >
                                  {moment(comment?.createdOn).format("LLL")}
                                </Typography>
                              </Box>
                            </Box>
                          ))
                        ) : (
                          <ListItem>
                            <ListItemText
                              primary={
                                <React.Fragment>
                                  <Typography
                                    sx={{ display: "inline" }}
                                    component="span"
                                    variant="body2"
                                    color="text.primary"
                                  >
                                    No comment history
                                  </Typography>
                                </React.Fragment>
                              }
                            />
                          </ListItem>
                        )}
                      </List>
                    </Collapse>
                  </List>
                </Box>

                {/* File Comments Section - Improved UI 
                  Only show for non-Stage5Bonus OR if comments exist for Stage5Bonus */}
                {(bonusType !== "Stage5Bonus" || filecomments.length > 0) && (
                  <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <List>
                      <ListItem
                        button
                        onClick={() => setViewPending(!viewPending)}
                        sx={{ bgcolor: "grey.50", borderRadius: 1, px: { xs: 1.5, sm: 2 } }}
                      >
                        <ListItemText
                          primary="Pending Comments"
                          primaryTypographyProps={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                        />
                        {viewPending ? <ExpandLess /> : <ExpandMore />}
                      </ListItem>
                      <Collapse in={viewPending} timeout="auto">
                        <List sx={{ maxHeight: 300, overflow: "auto" }}>
                          {filecomments.length ? (
                            filecomments.map((fileComment) => (
                              <ListItem
                                sx={{ px: { xs: 1, sm: 2 } }}
                                secondaryAction={
                                  <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={() =>
                                      setFileComments(
                                        filecomments.filter(
                                          (commentText) =>
                                            commentText.Id !== fileComment.Id
                                        )
                                      )
                                    }
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                }
                              >
                                <ListItemText
                                  secondary={fileComment.Comment}
                                  primary={
                                    <React.Fragment>
                                      <Typography
                                        sx={{ display: "inline", fontSize: { xs: "0.85rem", sm: "0.875rem" } }}
                                        component="span"
                                        variant="body2"
                                        color="text.primary"
                                      >
                                        {fileComment.Name || ""}
                                      </Typography>
                                    </React.Fragment>
                                  }
                                  secondaryTypographyProps={{ fontSize: { xs: "0.75rem", sm: "0.8125rem" } }}
                                />
                              </ListItem>
                            ))
                          ) : (
                            <ListItem>
                              <ListItemText
                                primary={
                                  <React.Fragment>
                                    <Typography
                                      sx={{ display: "inline", fontSize: { xs: "0.85rem", sm: "0.875rem" } }}
                                      component="span"
                                      variant="body2"
                                      color="text.primary"
                                    >
                                      No pending comments
                                    </Typography>
                                  </React.Fragment>
                                }
                              />
                            </ListItem>
                          )}
                        </List>
                      </Collapse>
                    </List>
                  </Box>
                )}

                {/* Review Options Section */}

                {!(job.reviewStatus === "OpsApproved" ||
                  job.reviewStatus === "InPayrollProcess" ||
                  job.reviewStatus === "Paid") && (
                    <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: "grey.50" }}>
                      <FormControl fullWidth>
                        <FormLabel id="review-options-label" sx={{ mb: 1, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                          Review Decision
                        </FormLabel>
                        <RadioGroup
                          value={reviewDecision}
                          onChange={(e) => setReviewDecision(e.target.value)}
                        >
                          <FormControlLabel
                            value="approve"
                            control={<Radio color="success" />}
                            label="Approve"
                            sx={{ "& .MuiFormControlLabel-label": { fontSize: { xs: "0.85rem", sm: "1rem" } } }}
                            disabled={
                              bonusType !== "Stage5Bonus" &&
                              (
                                // Disable if no builders assigned
                                !job?.builders || job.builders.length === 0 ||
                                // For OpsPlus role, also require valid close date
                                ((selectedRole === "OpsPlus" || selectedRole === "Ops") &&
                                  (!job?.closeDate || job.closeDate === "0001-01-01T00:00:00"))
                              )
                            }
                          />
                          {(bonusType === "ClosingBonus" ||
                            (bonusType === "Stage5Bonus" && selectedRole === "OpsPlus" && job.reviewStatus !== "OpsApproved"))
                            && (
                            <FormControlLabel
                              value="reject"
                              control={<Radio color="error" />}
                              label="Reject (Comment required)"
                              sx={{ "& .MuiFormControlLabel-label": { fontSize: { xs: "0.85rem", sm: "1rem" } } }}
                            />
                          )}
                        </RadioGroup>
                      </FormControl>

                      <TextField
                        multiline
                        fullWidth
                        minRows={3}
                        label="General Comment"
                        variant="outlined"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        sx={{
                          mt: 2,
                          mb: 2,
                          "& .MuiInputLabel-root": { fontSize: { xs: "0.85rem", sm: "1rem" } },
                          "& .MuiInputBase-root": { fontSize: { xs: "0.85rem", sm: "1rem" } }
                        }}
                      />

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{ fontSize: { xs: "0.85rem", sm: "0.9375rem" } }}
                      >
                        Submit Review
                      </Button>
                    </Box>
                  )}
                {(job.reviewStatus === "OpsApproved" && selectedRole === "OpsPlus") && (
                  <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: "grey.50" }}>
                    <FormControl fullWidth>
                      <FormLabel id="review-options-label" sx={{ mb: 1, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                        Review Decision
                      </FormLabel>
                      <RadioGroup
                        value={reviewDecision}
                        onChange={(e) => setReviewDecision(e.target.value)}
                      >
                        <FormControlLabel
                          value="opsApprovedToInOpsReview"
                          control={<Radio color="success" />}
                          label="Revert to Ops Review (for corrections)"
                          sx={{ "& .MuiFormControlLabel-label": { fontSize: { xs: "0.85rem", sm: "1rem" } } }}
                        />
                        <FormControlLabel
                          value="noActionJustComment"
                          control={<Radio color="success" />}
                          label="No Action - Just add a comment"
                          sx={{ "& .MuiFormControlLabel-label": { fontSize: { xs: "0.85rem", sm: "1rem" } } }}
                        />
                      </RadioGroup>
                    </FormControl>

                    <TextField
                      multiline
                      fullWidth
                      minRows={3}
                      label="General Comment"
                      variant="outlined"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      sx={{
                        mt: 2,
                        mb: 2,
                        "& .MuiInputLabel-root": { fontSize: { xs: "0.85rem", sm: "1rem" } },
                        "& .MuiInputBase-root": { fontSize: { xs: "0.85rem", sm: "1rem" } }
                      }}
                    />

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSubmit2}
                      sx={{ fontSize: { xs: "0.85rem", sm: "0.9375rem" } }}
                    >
                      Submit
                    </Button>
                  </Box>
                )}
                {((job.reviewStatus === "OpsApproved" && selectedRole === "Ops") ||
                  job.reviewStatus === "InPayrollProcess" ||
                  job.reviewStatus === "Paid") && (
                    <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: "grey.50" }}>
                      <TextField
                        multiline
                        fullWidth
                        minRows={3}
                        label="General Comment"
                        variant="outlined"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        sx={{
                          mt: 2,
                          mb: 2,
                          "& .MuiInputLabel-root": { fontSize: { xs: "0.85rem", sm: "1rem" } },
                          "& .MuiInputBase-root": { fontSize: { xs: "0.85rem", sm: "1rem" } }
                        }}
                      />

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSubmit3}
                        sx={{ fontSize: { xs: "0.85rem", sm: "0.9375rem" } }}
                      >
                        Submit Review
                      </Button>
                    </Box>
                  )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Dialog>
    </>
  );
};

export default ReviewerViewModal;